import { useState, useEffect, useRef, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface Matterport3DViewerProps {
  matterportUrl: string;
  propertyName: string;
  className?: string;
}

export const Matterport3DViewer = ({ matterportUrl, propertyName, className = '' }: Matterport3DViewerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        const element = containerRef.current;
        if (element) {
          await element.requestFullscreen();
          setIsFullscreen(true);
          trackEvent('property_3d_tour_fullscreen', { 
            property: propertyName, 
            action: 'enter' 
          });
        }
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
        setIsFullscreen(false);
        trackEvent('property_3d_tour_fullscreen', { 
          property: propertyName, 
          action: 'exit' 
        });
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Fallback to component-level fullscreen
      setIsFullscreen(!isFullscreen);
    }
  }, [propertyName, isFullscreen]);

  useEffect(() => {
    // Track 3D tour view
    trackEvent('property_3d_tour_view', { property: propertyName });

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Listen for keyboard events
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [propertyName, toggleFullscreen]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const openInNewTab = () => {
    window.open(matterportUrl, '_blank');
    trackEvent('property_3d_tour_external', { property: propertyName });
  };

  if (hasError) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-muted/50 rounded-lg ${className}`}>
        <div className="text-center">
          <h3 className="font-display text-lg font-semibold text-foreground mb-2">
            3D Tour Unavailable
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            The virtual tour is temporarily unavailable. Please try again later or contact us for a personal tour.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={openInNewTab}
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Try External Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="text-center">
            <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
            <Skeleton className="h-4 w-32 mx-auto mb-2" />
            <Skeleton className="h-3 w-24 mx-auto" />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={toggleFullscreen}
          className="bg-white/90 hover:bg-white text-foreground"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={openInNewTab}
          className="bg-white/90 hover:bg-white text-foreground"
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>

      {/* Matterport Iframe */}
      <iframe
        src={matterportUrl}
        title={`3D Virtual Tour - ${propertyName}`}
        className={`w-full transition-all duration-300 ${
          isFullscreen ? 'h-screen' : 'h-[500px] lg:h-[600px]'
        }`}
        allow="fullscreen; xr-spatial-tracking"
        allowFullScreen
        onLoad={handleLoad}
        onError={handleError}
        style={{
          border: 'none',
          borderRadius: isFullscreen ? '0' : '0.5rem'
        }}
      />

      {/* Instructions Overlay */}
      {!isLoading && !hasError && (
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3 text-white text-sm">
            <p className="text-center">
              Use mouse to navigate • Scroll to zoom • Click and drag to look around • Press F11 for fullscreen
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
